import { supabase } from "./supabaseClient";

// Map the most common Supabase auth errors to clean, friendly messages. For
// anything unmapped, surface the ACTUAL Supabase error text (never a generic
// "Something went wrong") so real failures are diagnosable.
function clean(error) {
  const m = (error?.message || "").toLowerCase();
  if (m.includes("invalid login credentials")) return "Incorrect email or password.";
  if (m.includes("email not confirmed")) return "Please confirm your email before logging in.";
  if (m.includes("captcha")) return "Captcha check failed — please complete the verification and try again.";
  if (m.includes("invalid") && m.includes("email")) return "Please enter a valid email address.";
  if (m.includes("rate") || m.includes("too many")) return "Too many attempts. Please wait a moment and try again.";
  if (m.includes("password should be")) return "Password must be at least 6 characters.";
  return error?.message || "Something went wrong. Please try again.";
}

export async function signUp(email, password, captchaToken) {
  const { data, error } = await supabase.auth.signUp({
    email: email.trim(),
    password,
    options: { emailRedirectTo: `${window.location.origin}/login`, captchaToken },
  });
  if (error) throw new Error(clean(error));
  // With email confirmation enabled, data.session is normally null.
  return { user: data.user, confirmationRequired: !data.session };
}

export async function signIn(email, password, captchaToken) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
    options: { captchaToken },
  });
  if (error) throw new Error(clean(error));
  return data.session;
}

// Continue with Google via Emergent's managed Google sign-in (no Google Cloud
// project needed on our side). Full-page redirect to Emergent's hosted consent
// page; it returns to /auth/callback with a one-time session_id, which that page
// exchanges for a normal Supabase session via verifyOtp — so Google users still
// land in the same RoadRnr user/profile system as email/password users.
// REMINDER: DO NOT HARDCODE A DIFFERENT URL OR ADD FALLBACK REDIRECTS HERE.
export function signInWithGoogle() {
  const redirectUrl = `${window.location.origin}/auth/callback`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export async function requestPasswordReset(email, captchaToken) {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/reset-password`,
    captchaToken,
  });
  if (error) throw new Error(clean(error));
  return true;
}

export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw new Error(clean(error));
  return true;
}

export async function signOut() {
  await supabase.auth.signOut();
}

// Permanently deletes the signed-in user's account + all RoadRnr data via the
// backend (service_role, server-side only). The client just forwards the current
// access token; on success the caller signs out.
export async function deleteAccount() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Please sign in again.");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let detail = "Could not delete your account. Please try again.";
    try {
      detail = (await res.json())?.detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return true;
}
